import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { getTenants, createTenant, updateTenant, deleteTenant, apiFetch } from "../api/client";

const normalizePlanName = (plan) => {
    const name = String(plan || '').trim();
    if (!name || name.toLowerCase() === 'free') return 'Basic';
    return name;
};

export default function SuperadminTenants() {
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", slug: "", isActive: true });
    const [plans, setPlans] = useState([]);
    const [subscription, setSubscription] = useState(null);
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [expiresAt, setExpiresAt] = useState('');

    useEffect(() => {
        let mounted = true;
        async function load() {
            try {
                const items = await getTenants();
                if (mounted) setTenants(items || []);
            } catch (e) {
                setError(e?.message || "Network error");
            } finally {
                if (mounted) setLoading(false);
            }
        }
        load();
        return () => { mounted = false; };
    }, []);

    useEffect(() => { fetchPlans(); }, []);

    async function fetchPlans() {
        try {
            const res = await apiFetch('/api/subscription/plans');
            if (!res.ok) return;
            const data = await res.json();
            const list = data?.plans || data?.data || [];
            setPlans(list || []);
        } catch (e) {
            // ignore
        }
    }

    // removed mobile sidebar state - sidebar is desktop-only

    const openCreate = () => { setEditing(null); setForm({ name: "", slug: "", isActive: true }); setShowModal(true); };
    const openEdit = (t) => { setEditing(t); setForm({ name: t.name || t.Name, slug: t.slug || t.Slug, isActive: t.isActive ?? t.IsActive }); setShowModal(true); };
    const closeModal = () => { setShowModal(false); };

    // load subscription when editing modal opens
    useEffect(() => {
        if (!showModal || !editing) { setSubscription(null); setSelectedPlanId(null); setExpiresAt(''); return; }
        let mounted = true;
        (async () => {
            try {
                const id = editing.tenantId || editing.TenantId;
                const res = await apiFetch(`/api/superadmin/tenants/${id}/subscription`, { method: 'GET', skipTenant: true });
                if (!res.ok) return;
                const body = await res.json().catch(() => null);
                const sub = body?.data || body?.Data || body;
                if (!mounted) return;
                setSubscription(sub);
                setSelectedPlanId(sub?.planId || sub?.PlanId || sub?.PlanId);
                setExpiresAt(sub?.expiresAt ? new Date(sub.expiresAt).toISOString().slice(0,16) : '');
            } catch (e) {
                // ignore
            }
        })();
        return () => { mounted = false; };
    }, [showModal, editing]);

    const submitForm = async () => {
        try {
            if (editing) {
                await updateTenant(editing.tenantId || editing.TenantId, { name: form.name, slug: form.slug, isActive: form.isActive });
                // apply subscription change if editing
                if (selectedPlanId) {
                    try {
                        const payload = { PlanId: selectedPlanId, ExpiresAt: expiresAt ? new Date(expiresAt).toISOString() : null, Status: 'Active' };
                        await apiFetch(`/api/superadmin/tenants/${editing.tenantId || editing.TenantId}/subscription`, { method: 'PUT', skipTenant: true, body: JSON.stringify(payload) });
                    } catch (e) {
                        // ignore subscription update error but continue
                    }
                }
            } else {
                await createTenant({ name: form.name, slug: form.slug, isActive: form.isActive });
            }
            const items = await getTenants();
            setTenants(items || []);
            setShowModal(false);
        } catch (e) {
            setError(e?.message || "Save failed");
        }
    };

    const handleDelete = async (t) => {
        try {
            if (!window.confirm(`Delete tenant ${t.name || t.Name}? This is destructive.`)) return;
            await deleteTenant(t.tenantId || t.TenantId);
            const items = await getTenants();
            setTenants(items || []);
        } catch (e) {
            setError(e?.message || "Delete failed");
        }
    };

    if (loading) return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-xl text-gray-600">Loading...</div>
        </div>
    );
    if (error) return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-xl text-red-600">{error}</div>
        </div>
    );

    return (
        <>
        <Header />
        <div className="min-h-screen bg-gray-50">

            <div className="grid grid-cols-12">
                <aside className="col-span-2 hidden lg:flex">
                    <Sidebar />
                </aside>

                {/* mobile sidebar removed; sidebar is desktop-only */}

                <main className="col-span-12 lg:col-span-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="mb-6 flex items-center justify-between">
                        <div>
                            <h2 className="text-2xl font-bold">Tenant Management</h2>
                            <p className="text-sm text-gray-600">Manage tenants, their subscriptions and monitor activity.</p>
                        </div>
                        <div>
                            <button onClick={openCreate} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">Add Tenant</button>
                        </div>
                    </div>

                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stats</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription</th>
                                        <th className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {tenants.map(t => (
                                        <tr key={t.tenantId || t.TenantId} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                                        <span className="text-sm font-medium text-gray-700">{(t.name || t.Name || '').charAt(0).toUpperCase()}</span>
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium text-gray-900">{t.name || t.Name}</div>
                                                        <div className="text-sm text-gray-500">/{t.slug || t.Slug}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900 grid grid-cols-2 gap-4">
                                                    <div><span className="font-medium">{t.userCount || t.UserCount || 0}</span><span className="text-gray-500 ml-1"> users</span></div>
                                                    <div><span className="font-medium">{t.productCount || t.ProductCount || 0}</span><span className="text-gray-500 ml-1"> products</span></div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">
                                                    <div className="font-medium">{normalizePlanName(t.subscriptionPlan || t.SubscriptionPlan)}</div>
                                                    {t.subscriptionExpiresAt || t.SubscriptionExpiresAt ? (
                                                        <div className="text-gray-500">Expires: {new Date(t.subscriptionExpiresAt || t.SubscriptionExpiresAt).toLocaleDateString()}</div>
                                                    ) : null}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button onClick={() => openEdit(t)} className="text-indigo-600 hover:text-indigo-900 mr-4">Edit</button>
                                                <button onClick={() => handleDelete(t)} className="text-red-600 hover:text-red-900">Delete</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {showModal && (
                        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
                            <div className="relative mx-auto p-6 border w-full max-w-md shadow-lg rounded-lg bg-white">
                                <div className="mt-2">
                                    <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                                        <h3 className="text-lg font-medium text-gray-900">{editing ? 'Edit Tenant' : 'Add New Tenant'}</h3>
                                        <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 transition-colors text-2xl leading-none">&times;</button>
                                    </div>
                                    <div className="mt-5">
                                        <div className="mb-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Tenant Name</label>
                                            <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" />
                                        </div>
                                        <div className="mb-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Tenant Slug</label>
                                            <input type="text" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" />
                                        </div>
                                        <div className="mb-4">
                                            <label className="inline-flex items-center">
                                                <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} className="mr-2 rounded" /> 
                                                <span className="text-sm font-medium text-gray-700">Active</span>
                                            </label>
                                        </div>
                                        {editing && (
                                            <div className="mb-4 border-t pt-4">
                                                <label className="block text-sm font-medium text-gray-700 mb-3">Subscription (admin)</label>
                                                <div className="space-y-3">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-600 mb-1">Plan</label>
                                                        <select value={selectedPlanId || ''} onChange={e => setSelectedPlanId(Number(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
                                                            <option value="">Select plan</option>
                                                            {plans.map(p => (
                                                                <option key={p.planId || p.PlanId} value={p.planId || p.PlanId}>{(p.name || p.Name) + ' (' + ((p.itemLimit != null && p.itemLimit !== undefined) ? p.itemLimit + ' items' : 'unlimited') + ')'}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-600 mb-1">Expires At</label>
                                                        <input type="datetime-local" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex justify-end gap-3 pt-5 border-t border-gray-200 mt-5">
                                        <button onClick={closeModal} className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors">Cancel</button>
                                        <button onClick={submitForm} className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors">{editing ? 'Update Tenant' : 'Create Tenant'}</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
        </>
    );
}
